<template>
    <div class="mx-2 mx-lg-5 mb-5">
        <div v-if="section === 'info'" class="card mt-3">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>{{ gatehouse.name }}</h3>
                <div>
                    <button class="btn btn-sm btn-secondary me-3" @click="editGatehouse">
                        <icon icon="EditPen"></icon>
                    </button>
                    <button class="btn btn-sm btn-danger" @click="deleteGatehouse">
                        <icon icon="Trash"></icon>
                    </button>
                </div>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("gatehouse.name") }}</dt>
                            <dd class="col-sm-8">{{ gatehouse.name }}</dd>

                            <dt class="col-sm-4">{{ $t("gatehouse.address") }}</dt>
                            <dd class="col-sm-8">{{ gatehouse.address }}</dd>
                        </dl>
                    </div>
                    <div class="col-md-6">
                        <dl class="row mb-0">
                            <dt class="col-sm-4">{{ $t("gatehouse.qr") }}</dt>
                            <dd class="col-sm-8">
                                <img
                                    v-if="gatehouse.qr_img"
                                    :src="getQr"
                                    alt="QR kód"
                                    width="180"
                                />
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="section === 'settings'" class="card mt-3">
            <div class="card-header">
                <h5 class="mb-0">{{ $t("gatehouse.settings") }}</h5>
            </div>
            <div class="card-body">
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="carrierMultiselect"
                        :checked="gatehouse.carrier_multiselect"
                        @change="toggleCarrierMultiselect"
                    >
                    <label class="form-check-label fw-bold" for="carrierMultiselect">
                        <span>{{ $t("gatehouse.carrierMultiselect") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.carrierMultiselectDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookNameMultiselect"
                        :checked="gatehouse.guestbook_name_multiselect"
                        @change="toggleGuestbookNameMultiselect"
                    >
                    <label class="form-check-label fw-bold" for="guestbookNameMultiselect">
                        <span>{{ $t("gatehouse.guestbookNameMultiselect") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookNameMultiselectDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookFirstNameRequired"
                        :checked="gatehouse.guestbook_first_name_required"
                        :disabled="!gatehouse.guestbook_first_name_visible"
                        @change="toggleGuestbookFirstNameRequired"
                    >
                    <label class="form-check-label fw-bold" for="guestbookFirstNameRequired">
                        <span>{{ $t("gatehouse.guestbookFirstNameRequired") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookFirstNameRequiredDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookPhoneRequired"
                        :checked="gatehouse.guestbook_phone_required"
                        :disabled="!gatehouse.guestbook_phone_visible"
                        @change="toggleGuestbookPhoneRequired"
                    >
                    <label class="form-check-label fw-bold" for="guestbookPhoneRequired">
                        <span>{{ $t("gatehouse.guestbookPhoneRequired") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookPhoneRequiredDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookFirstNameVisible"
                        :checked="gatehouse.guestbook_first_name_visible"
                        @change="toggleSetting('guestbook_first_name_visible')"
                    >
                    <label class="form-check-label fw-bold" for="guestbookFirstNameVisible">
                        <span>{{ $t("gatehouse.guestbookFirstNameVisible") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookFirstNameVisibleDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookPhoneVisible"
                        :checked="gatehouse.guestbook_phone_visible"
                        @change="toggleSetting('guestbook_phone_visible')"
                    >
                    <label class="form-check-label fw-bold" for="guestbookPhoneVisible">
                        <span>{{ $t("gatehouse.guestbookPhoneVisible") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookPhoneVisibleDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="vehicleDriverFirstNameVisible"
                        :checked="gatehouse.vehicle_driver_first_name_visible"
                        @change="toggleSetting('vehicle_driver_first_name_visible')"
                    >
                    <label class="form-check-label fw-bold" for="vehicleDriverFirstNameVisible">
                        <span>{{ $t("gatehouse.vehicleDriverFirstNameVisible") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.vehicleDriverFirstNameVisibleDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="vehicleDriverFirstNameRequired"
                        :checked="gatehouse.vehicle_driver_first_name_required"
                        :disabled="!gatehouse.vehicle_driver_first_name_visible"
                        @change="toggleSetting('vehicle_driver_first_name_required')"
                    >
                    <label class="form-check-label fw-bold" for="vehicleDriverFirstNameRequired">
                        <span>{{ $t("gatehouse.vehicleDriverFirstNameRequired") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.vehicleDriverFirstNameRequiredDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="vehicleDriverPhoneVisible"
                        :checked="gatehouse.vehicle_driver_phone_visible"
                        @change="toggleSetting('vehicle_driver_phone_visible')"
                    >
                    <label class="form-check-label fw-bold" for="vehicleDriverPhoneVisible">
                        <span>{{ $t("gatehouse.vehicleDriverPhoneVisible") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.vehicleDriverPhoneVisibleDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="vehicleDriverPhoneRequired"
                        :checked="gatehouse.vehicle_driver_phone_required"
                        :disabled="!gatehouse.vehicle_driver_phone_visible"
                        @change="toggleSetting('vehicle_driver_phone_required')"
                    >
                    <label class="form-check-label fw-bold" for="vehicleDriverPhoneRequired">
                        <span>{{ $t("gatehouse.vehicleDriverPhoneRequired") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.vehicleDriverPhoneRequiredDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <hr>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="vehicleNfcEnabled"
                        :checked="gatehouse.vehicle_nfc_enabled"
                        @change="toggleVehicleNfcEnabled"
                    >
                    <label class="form-check-label fw-bold" for="vehicleNfcEnabled">
                        <span>{{ $t("gatehouse.vehicleNfcEnabled") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.vehicleNfcEnabledDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="bozpEnabled"
                        :checked="gatehouse.bozp_enabled"
                        @change="toggleBozpEnabled"
                    >
                    <label class="form-check-label fw-bold" for="bozpEnabled">
                        <span>{{ $t("gatehouse.bozpEnabled") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.bozpEnabledDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="evacuationListEnabled"
                        :checked="gatehouse.evacuation_list_enabled"
                        @change="toggleEvacuationListEnabled"
                    >
                    <label class="form-check-label fw-bold" for="evacuationListEnabled">
                        <span>{{ $t("gatehouse.evacuationListEnabled") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.evacuationListEnabledDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="evacuationListSecurityEnabled"
                        :checked="gatehouse.evacuation_list_security_enabled"
                        :disabled="!gatehouse.evacuation_list_enabled"
                        @change="toggleSetting('evacuation_list_security_enabled')"
                    >
                    <label class="form-check-label fw-bold" for="evacuationListSecurityEnabled">
                        <span>{{ $t("gatehouse.evacuationListSecurityEnabled") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.evacuationListSecurityEnabledDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
                <div class="form-check form-switch mb-3 filter_switch">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="guestbookOverviewExportEnabled"
                        :checked="gatehouse.guestbook_overview_export_enabled"
                        @change="toggleGuestbookOverviewExportEnabled"
                    >
                    <label class="form-check-label fw-bold" for="guestbookOverviewExportEnabled">
                        <span>{{ $t("gatehouse.guestbookOverviewExportEnabled") }}</span>
                        <span>
                            <small class="text-muted ms-1">
                                ({{ $t("gatehouse.guestbookOverviewExportEnabledDesc") }})
                            </small>
                        </span>
                    </label>
                </div>
            </div>
        </div>

        <template v-if="section === 'bozp'">
            <BozpSettings v-if="gatehouse.bozp_enabled" />
            <div v-else-if="loaded" class="alert alert-secondary mt-3">
                {{ $t("gatehouse.bozpDisabled") }}
                <router-link :to="{ name: 'GatehouseSettingsView', params: { slug: $route.params.slug } }">
                    {{ $t("navbar.gatehouseSettings") }}
                </router-link>
            </div>
        </template>

        <div v-if="section === 'info'" class="card mt-3">
            <div class="card-header">
                <h5 class="mb-0">{{ $t("gatehouse.exports.title") }}</h5>
            </div>
            <div class="card-body">
                <button class="btn btn-sm btn-outline-primary mb-3" @click="openExportModal">
                    {{ $t("gatehouse.exports.manualExport") }}
                </button>
                <div
                    v-for="(exportConfig, key) in exports"
                    :key="key"
                    class="export-section mb-4"
                    :class="{ 'pb-4 border-bottom': key !== lastExportKey }"
                >
                    <div class="form-check form-switch mb-3 filter_switch">
                        <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`export_${key}`"
                            :checked="exportConfig.enabled"
                            @change="toggleExportEnabled(key)"
                        >
                        <label class="form-check-label fw-bold" :for="`export_${key}`">
                            {{ $t(`gatehouse.exports.${key}`) }}
                            <small class="text-muted fw-normal ms-1">({{ $t("gatehouse.exports.automaticSending") }})</small>
                        </label>
                    </div>

                    <!-- recipients are shown even with automatic sending off, the guestbook ones also get manual exports -->
                    <div class="ms-5">
                        <div v-if="exportConfig.enabled" class="d-flex gap-3 mb-3">
                            <div v-for="freq in ['daily', 'weekly', 'monthly']" :key="freq" class="form-check">
                                <input
                                    class="form-check-input"
                                    type="radio"
                                    :id="`freq_${key}_${freq}`"
                                    :name="`freq_${key}`"
                                    :value="freq"
                                    :checked="exportConfig.frequency === freq"
                                    @change="setExportFrequency(key, freq)"
                                >
                                <label class="form-check-label" :for="`freq_${key}_${freq}`">
                                    {{ $t(`gatehouse.exports.frequency.${freq}`) }}
                                </label>
                            </div>
                        </div>
                        <div
                            v-for="(emailObj, idx) in exportConfig.emails"
                            :key="idx"
                            class="d-flex align-items-center gap-2 mb-2"
                        >
                            <span style="min-width: 200px;">{{ emailObj.email }}</span>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                style="max-width: 200px;"
                                :placeholder="$t('gatehouse.exports.passwordPlaceholder')"
                                v-model="emailObj.pdf_password"
                                @change="saveExportEmails(key)"
                            >
                            <button
                                class="btn btn-outline-danger btn-sm text-nowrap"
                                @click="removeEmail(key, idx)"
                            >
                                {{ $t("utils.delete") }}
                            </button>
                        </div>

                        <div v-if="exportConfig.addingEmail" class="d-flex gap-2 mt-2 flex-wrap" style="max-width: 620px;">
                            <input
                                type="email"
                                class="form-control form-control-sm"
                                style="max-width: 240px;"
                                :placeholder="$t('gatehouse.exports.emailPlaceholder')"
                                v-model="exportConfig.newEmail"
                                @keyup.enter="addEmail(key)"
                                @keyup.esc="cancelAddEmail(key)"
                                ref="emailInput"
                                autofocus
                            >
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                style="max-width: 200px;"
                                :placeholder="$t('gatehouse.exports.passwordPlaceholder')"
                                v-model="exportConfig.newPassword"
                                @keyup.enter="addEmail(key)"
                                @keyup.esc="cancelAddEmail(key)"
                            >
                            <button
                                class="btn btn-sm btn-primary text-nowrap"
                                @click="addEmail(key)"
                                :disabled="!exportConfig.newEmail"
                            >
                                {{ $t("gatehouse.exports.addEmail") }}
                            </button>
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelAddEmail(key)"
                            >
                                {{ $t("utils.cancel") }}
                            </button>
                        </div>
                        <button
                            v-if="!exportConfig.addingEmail"
                            class="btn btn-sm btn-outline-primary mt-1"
                            @click="exportConfig.addingEmail = true"
                        >
                            + {{ $t("gatehouse.exports.addEmail") }}
                        </button>
                        <p v-if="key === 'guestbook'" class="text-muted small mt-2 mb-0">
                            {{ $t("gatehouse.exports.guestbookManualHint") }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="section === 'info' && gatehouse.evacuation_list_enabled" class="card mt-3">
            <div class="card-header">
                <h5 class="mb-0">{{ $t("gatehouse.evacuation.title") }}</h5>
            </div>
            <div class="card-body">
                <p class="text-muted">{{ $t("gatehouse.evacuation.description") }}</p>
                <div
                    v-for="(recipient, idx) in evacuation.emails"
                    :key="idx"
                    class="d-flex flex-wrap align-items-center gap-2 mb-2"
                >
                    <span style="min-width: 200px;">{{ recipient.email }}</span>
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        style="max-width: 200px;"
                        :placeholder="$t('gatehouse.evacuation.passwordPlaceholder')"
                        v-model="recipient.pdf_password"
                        @change="saveEvacuationEmails"
                    >
                    <button class="btn btn-outline-secondary btn-sm text-nowrap" @click="regenerateEvacuationPassword(idx)">
                        {{ $t("gatehouse.evacuation.generate") }}
                    </button>
                    <button class="btn btn-outline-danger btn-sm text-nowrap" @click="removeEvacuationEmail(idx)">
                        {{ $t("utils.delete") }}
                    </button>
                </div>

                <div v-if="evacuation.addingEmail" class="d-flex gap-2 mt-2 flex-wrap" style="max-width: 720px;">
                    <input
                        type="email"
                        class="form-control form-control-sm"
                        style="max-width: 240px;"
                        :placeholder="$t('gatehouse.exports.emailPlaceholder')"
                        v-model="evacuation.newEmail"
                        @keyup.esc="cancelAddEvacuationEmail"
                    >
                    <input
                        type="text"
                        class="form-control form-control-sm"
                        style="max-width: 200px;"
                        :placeholder="$t('gatehouse.evacuation.passwordPlaceholder')"
                        v-model="evacuation.newPassword"
                        @keyup.esc="cancelAddEvacuationEmail"
                    >
                    <button class="btn btn-sm btn-outline-secondary text-nowrap" @click="evacuation.newPassword = generatePassword()">
                        {{ $t("gatehouse.evacuation.generate") }}
                    </button>
                    <button
                        class="btn btn-sm btn-primary text-nowrap"
                        @click="addEvacuationEmail"
                        :disabled="!evacuation.newEmail.trim() || !evacuation.newPassword.trim()"
                    >
                        {{ $t("gatehouse.exports.addEmail") }}
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="cancelAddEvacuationEmail">
                        {{ $t("utils.cancel") }}
                    </button>
                </div>
                <button
                    v-if="!evacuation.addingEmail"
                    class="btn btn-sm btn-outline-primary mt-1"
                    @click="evacuation.addingEmail = true"
                >
                    + {{ $t("gatehouse.exports.addEmail") }}
                </button>
                <div v-if="evacuation.error" class="text-danger small mt-2">{{ evacuation.error }}</div>
            </div>
        </div>
    </div>

    <!-- Export modal -->
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="exportModal">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header justify-content-between align-items-center border-0 mx-2">
                    <h5 class="modal-title">{{ $t("gatehouse.exports.manualExport") }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-2">
                    <div class="mb-3">
                        <label class="form-label">{{ $t("gatehouse.exports.exportType") }}</label>
                        <select class="form-select" v-model="exportForm.type" required>
                            <option value="patrol_log">{{ $t("gatehouse.exports.patrolLog") }}</option>
                            <option value="guestbook">{{ $t("gatehouse.exports.guestbook") }}</option>
                            <option value="vehicle_entry">{{ $t("gatehouse.exports.vehicleEntry") }}</option>
                        </select>
                    </div>

                    <div class="mb-3 d-flex gap-2">
                        <button class="btn btn-sm btn-outline-secondary" @click="setExportToday">
                            {{ $t("gatehouse.exports.today") }}
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="setExportYesterday">
                            {{ $t("gatehouse.exports.yesterday") }}
                        </button>
                    </div>

                    <div class="row g-2">
                        <div class="col-6">
                            <label class="form-label">{{ $t("gatehouse.exports.dateFrom") }}</label>
                            <input type="date" class="form-control" v-model="exportForm.dateFrom" required>
                        </div>
                        <div class="col-6">
                            <label class="form-label">{{ $t("gatehouse.exports.dateTo") }}</label>
                            <input type="date" class="form-control" v-model="exportForm.dateTo" required>
                        </div>
                    </div>
                </div>
                <div class="modal-footer border-0">
                    <button
                        class="btn btn-primary"
                        @click="generateExport"
                        :disabled="!exportForm.dateFrom || !exportForm.dateTo || exportLoading"
                    >
                        <span v-if="exportLoading" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("gatehouse.exports.generate") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import { CONFIG } from "@/config.js"
import Icon from '@/components/Icon.vue';
import BozpSettings from '@/components/BozpSettings.vue';
import { Modal } from 'bootstrap';

export default {
    components: {
        Icon,
        BozpSettings,
    },
    data() {
        return {
            gatehouse: {},
            loaded: false,
            exports: {
                guestbook:    { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'guestbook', frequency: 'daily' },
                vehicleEntry: { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'vehicle_entry', frequency: 'daily' },
                patrolLog:    { enabled: false, emails: [], newEmail: '', newPassword: '', addingEmail: false, apiKey: 'patrol_log', frequency: 'daily' },
            },
            evacuation: { emails: [], saved: [], newEmail: '', newPassword: '', addingEmail: false, error: '' },
            exportModal: null,
            exportLoading: false,
            exportForm: {
                type: 'patrol_log',
                dateFrom: '',
                dateTo: '',
            },
        }
    },
    mounted() {
        this.exportModal = new Modal(this.$refs.exportModal);
    },
    methods: {
        getGatehouse() {
            this.$store.getters.api.get(`/dashboard/${this.$route.params.slug}/gatehouse`)
            .then(response => {
                this.gatehouse = response.data;
                this.loaded = true;
                this.loadExports();
            })
            .catch(error => {
                console.error("Error loading gatehouse. ", error)
            });
        },
        loadExports() {
            for (const config of Object.values(this.exports)) {
                config.enabled = this.gatehouse[`${config.apiKey}_export_enabled`] ?? false;
                config.emails = [...(this.gatehouse[`${config.apiKey}_export_emails`] ?? [])];
                config.frequency = this.gatehouse[`${config.apiKey}_export_frequency`] ?? 'daily';
            }
            this.evacuation.saved = (this.gatehouse.evacuation_export_emails ?? []).map(r => ({ ...r }));
            this.evacuation.emails = this.evacuation.saved.map(r => ({ ...r }));
        },
        editGatehouse() {
            this.$router.push({
                name: "GatehouseEditView",
                params: {
                    slug: this.$route.params.slug,
                    id: this.gatehouseId
                }
            });
        },
        toggleSetting(field) {
            const newValue = !this.gatehouse[field];
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [field]: newValue
            })
            .then(() => {
                this.gatehouse[field] = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { [field]: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleGuestbookNameMultiselect() {
            const newValue = !this.gatehouse.guestbook_name_multiselect;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                guestbook_name_multiselect: newValue
            })
            .then(() => {
                this.gatehouse.guestbook_name_multiselect = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { guestbook_name_multiselect: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleCarrierMultiselect() {
            const newValue = !this.gatehouse.carrier_multiselect;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                carrier_multiselect: newValue
            })
            .then(() => {
                this.gatehouse.carrier_multiselect = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { carrier_multiselect: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleGuestbookFirstNameRequired() {
            const newValue = !this.gatehouse.guestbook_first_name_required;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                guestbook_first_name_required: newValue
            })
            .then(() => {
                this.gatehouse.guestbook_first_name_required = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { guestbook_first_name_required: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleGuestbookPhoneRequired() {
            const newValue = !this.gatehouse.guestbook_phone_required;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                guestbook_phone_required: newValue
            })
            .then(() => {
                this.gatehouse.guestbook_phone_required = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { guestbook_phone_required: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleVehicleNfcEnabled() {
            const newValue = !this.gatehouse.vehicle_nfc_enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                vehicle_nfc_enabled: newValue
            })
            .then(() => {
                this.gatehouse.vehicle_nfc_enabled = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { vehicle_nfc_enabled: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleBozpEnabled() {
            const newValue = !this.gatehouse.bozp_enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                bozp_enabled: newValue
            })
            .then(() => {
                this.gatehouse.bozp_enabled = newValue;
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleGuestbookOverviewExportEnabled() {
            const newValue = !this.gatehouse.guestbook_overview_export_enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                guestbook_overview_export_enabled: newValue
            })
            .then(() => {
                this.gatehouse.guestbook_overview_export_enabled = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { guestbook_overview_export_enabled: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        toggleEvacuationListEnabled() {
            const newValue = !this.gatehouse.evacuation_list_enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                evacuation_list_enabled: newValue
            })
            .then(() => {
                this.gatehouse.evacuation_list_enabled = newValue;
                this.$store.commit("updateGatehouseSettings", {
                    slug: this.$route.params.slug,
                    settings: { evacuation_list_enabled: newValue }
                });
            })
            .catch(error => {
                console.error("Error updating gatehouse settings.", error);
            });
        },
        setExportFrequency(key, frequency) {
            const config = this.exports[key];
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_frequency`]: frequency,
            })
            .then(() => {
                config.frequency = frequency;
            })
            .catch(error => {
                console.error("Error updating export frequency.", error);
            });
        },
        toggleExportEnabled(key) {
            const config = this.exports[key];
            const newValue = !config.enabled;
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_enabled`]: newValue,
            })
            .then(() => {
                config.enabled = newValue;
            })
            .catch(error => {
                console.error("Error updating export settings.", error);
            });
        },
        saveExportEmails(key) {
            const config = this.exports[key];
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                [`${config.apiKey}_export_emails`]: config.emails,
            })
            .catch(error => {
                console.error("Error saving export emails.", error);
            });
        },
        addEmail(key) {
            const email = this.exports[key].newEmail.trim();
            if (!email) return;
            this.exports[key].emails.push({ email, pdf_password: this.exports[key].newPassword.trim() });
            this.exports[key].newEmail = '';
            this.exports[key].newPassword = '';
            this.exports[key].addingEmail = false;
            this.saveExportEmails(key);
        },
        cancelAddEmail(key) {
            this.exports[key].newEmail = '';
            this.exports[key].newPassword = '';
            this.exports[key].addingEmail = false;
        },
        removeEmail(key, idx) {
            this.exports[key].emails.splice(idx, 1);
            this.saveExportEmails(key);
        },
        generatePassword() {
            // no look-alike characters, the admin passes the password on by phone or SMS
            const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
            return Array.from(crypto.getRandomValues(new Uint32Array(16)), value => chars[value % chars.length]).join("");
        },
        saveEvacuationEmails() {
            // the password is dictated to the recipient, stray spaces would make the PDF impossible to open
            this.evacuation.emails.forEach(recipient => { recipient.pdf_password = recipient.pdf_password.trim(); });
            if (this.evacuation.emails.some(recipient => !recipient.pdf_password)) {
                // nothing is saved, so the screen goes back to what the server has
                this.evacuation.emails = this.evacuation.saved.map(r => ({ ...r }));
                this.evacuation.error = this.$t("gatehouse.evacuation.passwordRequired");
                return;
            }
            this.evacuation.error = '';
            this.$store.getters.api.patch(`/dashboard/${this.$route.params.slug}/gatehouse/settings/edit`, {
                evacuation_export_emails: this.evacuation.emails,
            })
            .then(response => {
                this.evacuation.saved = response.data.evacuation_export_emails.map(r => ({ ...r }));
            })
            .catch(error => {
                console.error("Error saving evacuation list recipients.", error);
                this.evacuation.emails = this.evacuation.saved.map(r => ({ ...r }));
                this.evacuation.error = this.$t("gatehouse.evacuation.saveError");
            });
        },
        addEvacuationEmail() {
            this.evacuation.emails.push({
                email: this.evacuation.newEmail.trim(),
                pdf_password: this.evacuation.newPassword.trim(),
            });
            this.cancelAddEvacuationEmail();
            this.saveEvacuationEmails();
        },
        cancelAddEvacuationEmail() {
            this.evacuation.newEmail = '';
            this.evacuation.newPassword = '';
            this.evacuation.addingEmail = false;
        },
        regenerateEvacuationPassword(idx) {
            this.evacuation.emails[idx].pdf_password = this.generatePassword();
            this.saveEvacuationEmails();
        },
        removeEvacuationEmail(idx) {
            this.evacuation.emails.splice(idx, 1);
            this.saveEvacuationEmails();
        },
        openExportModal() {
            this.exportModal.show();
        },
        setExportToday() {
            const today = new Date().toISOString().split('T')[0];
            this.exportForm.dateFrom = today;
            this.exportForm.dateTo = today;
        },
        setExportYesterday() {
            const d = new Date();
            d.setDate(d.getDate() - 1);
            const yesterday = d.toISOString().split('T')[0];
            this.exportForm.dateFrom = yesterday;
            this.exportForm.dateTo = yesterday;
        },
        generateExport() {
            this.exportLoading = true;
            this.$store.getters.api.get(
                `/dashboard/${this.$route.params.slug}/gatehouse/export`,
                {
                    params: {
                        type: this.exportForm.type,
                        date_from: this.exportForm.dateFrom,
                        date_to: this.exportForm.dateTo,
                    },
                    responseType: 'blob',
                }
            )
            .then(response => {
                const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `export_${this.exportForm.type}_${this.exportForm.dateFrom}_${this.exportForm.dateTo}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                this.exportModal.hide();
            })
            .catch(error => {
                console.error('Export error:', error);
            })
            .finally(() => {
                this.exportLoading = false;
            });
        },
        deleteGatehouse() {
            const confirm = window.confirm(this.$t("utils.deleteConfirm", { name: this.gatehouse.name }));
            if (!confirm) return;

            this.$store.getters.api.delete(`/dashboard/${this.$route.params.slug}/gatehouse/${this.gatehouseId}/delete`)
            .then(() => {
                this.$store.commit("removeGatehouse", this.gatehouse.slug);

                const slug = this.$store.getters.gatehouses[0].slug;
                this.$router.push({
                    name: "GatehouseView",
                    params: {
                        slug: slug,
                    }
                });
            })
            .catch(error => {
                console.error("Error deleting gatehouse.", error);
            });
        },
    },
    computed: {
        section() {
            return this.$route.meta.section || "info";
        },
        getQr() {
            const slug = this.$route.params.slug;
            const url = CONFIG.api.baseUrl;
            return `${url}/dashboard/${slug}/gatehouse/qr-code-img`;
        },
        gatehouseId() {
            return this.gatehouse.id;
        },
        lastExportKey() {
            const keys = Object.keys(this.exports);
            return keys[keys.length - 1];
        }
    },
    watch: {
        "$route.params.slug": {
            immediate: true,
            handler(newSlug) {
                this.getGatehouse();
            }
        }
    }
}
</script>

<style scoped>
.filter_switch {
    .form-check-input {
        width: 40px;
        height: 20px;
        cursor: pointer;
    }
    .form-check-label {
        margin-left: 12px;
        font-size: 16px;
        cursor: pointer;
    }
}
</style>